import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  SparklesIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  ForwardIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/solid";

const SKIP_AFTER_SECONDS = 5;

export default function AdVideoModal({
  adData,
  isOpen,
  onComplete,
  onClose,
}) {
  const videoRef = useRef(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const [canSkip, setCanSkip] = useState(false);
  const [skipTimer, setSkipTimer] = useState(SKIP_AFTER_SECONDS);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isBuffering, setIsBuffering] = useState(true);
  const [hasFinished, setHasFinished] = useState(false);

  const clip = adData?.clip;
  const category = adData?.category;

  // Reset state when a new ad opens
  useEffect(() => {
    if (isOpen) {
      setCurrentTime(0);
      setDuration(0);
      setCanSkip(false);
      setSkipTimer(SKIP_AFTER_SECONDS);
      setIsBuffering(true);
      setHasFinished(false);

      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        // Attempt autoplay with muted fallback for strict mobile/browser policies
        videoRef.current.muted = true;
        setIsMuted(true);
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => setIsPlaying(true))
            .catch(() => {
              setIsPlaying(false);
              setIsBuffering(false);
            });
        }
      }
    }
  }, [isOpen, clip?.id]);

  // Handle countdown for skip eligibility
  useEffect(() => {
    if (!isOpen || canSkip) return;

    const timer = setInterval(() => {
      setSkipTimer((prev) => {
        if (prev <= 1) {
          setCanSkip(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, canSkip]);

  if (!isOpen || !clip) return null;

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
      if (isBuffering) setIsBuffering(false);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration || 0);
      setIsBuffering(false);
    }
  };

  const handleVideoEnded = () => {
    setHasFinished(true);
    // Smooth transition: unlock after brief celebration state
    setTimeout(() => {
      onComplete?.();
    }, 600);
  };

  const handleSkipOrContinue = () => {
    onComplete?.();
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      const nextMuted = !videoRef.current.muted;
      videoRef.current.muted = nextMuted;
      setIsMuted(nextMuted);
    }
  };

  const remainingSeconds = Math.max(0, Math.ceil(duration - currentTime));
  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="fixed inset-0 z-[999999] bg-black/90 backdrop-blur-md flex items-center justify-center p-3 sm:p-6 animate-fade-in">
      <div className="w-full max-w-3xl bg-[#141414] border border-zinc-800 rounded-2xl overflow-hidden shadow-2xl relative flex flex-col transition-all">
        {/* Top Header Bar */}
        <div className="px-4 py-3 bg-[#111111] border-b border-zinc-800 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 overflow-hidden">
            <span className="flex-shrink-0 px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-amber-500/20 text-amber-300 border border-amber-500/40">
              Advertisement
            </span>
            <div className="truncate">
              <span className="text-white text-xs sm:text-sm font-semibold truncate block">
                {clip?.title || "Sponsored Clip"}
              </span>
              <span className="text-zinc-500 text-[10px] sm:text-xs block">
                {clip?.ad_category_name || category?.title || "BAB Digital Partner"}
              </span>
            </div>
          </div>

          {/* Go Ad-Free with VIP CTA */}
          <Link
            to="/Subscription"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all bg-gradient-to-r from-amber-500/20 to-secondary/20 hover:from-amber-500/30 hover:to-secondary/30 text-amber-300 border border-amber-500/40 shadow-sm hover:scale-[1.02] active:scale-95"
            title="Subscribe to BAB Premium for 100% ad-free streaming"
          >
            <SparklesIcon className="w-3.5 h-3.5 text-secondary" />
            <span className="hidden sm:inline">Go Ad-Free with</span> VIP
          </Link>
        </div>

        {/* Video Player Container */}
        <div
          className="relative aspect-video w-full bg-black flex items-center justify-center cursor-pointer select-none group"
          onClick={toggleMute}
        >
          <video
            ref={videoRef}
            src={clip?.ad_clip}
            playsInline
            autoPlay
            muted={isMuted}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onEnded={handleVideoEnded}
            onWaiting={() => setIsBuffering(true)}
            onPlaying={() => setIsBuffering(false)}
            onError={() => {
              // Graceful fallback: never trap the user if the ad clip cannot load
              console.warn("Ad clip playback error, granting access immediately.");
              onComplete?.();
            }}
            className="w-full h-full object-contain"
          />

          {/* Buffering Spinner */}
          {isBuffering && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40 pointer-events-none">
              <div className="w-12 h-12 border-3 border-secondary/30 border-t-secondary rounded-full animate-spin"></div>
            </div>
          )}

          {/* Unmute Floating Banner for Mobile / Default Muted */}
          {isMuted && !isBuffering && (
            <div className="absolute top-4 left-4 bg-black/75 backdrop-blur-sm border border-zinc-700/80 rounded-full px-3 py-1.5 flex items-center gap-1.5 text-xs text-white font-medium shadow-lg hover:border-secondary transition-colors pointer-events-auto">
              <SpeakerXMarkIcon className="w-4 h-4 text-secondary" />
              <span>Tap to Unmute</span>
            </div>
          )}

          {/* Finished Celebration Overlay */}
          {hasFinished && (
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex flex-col items-center justify-center text-center p-4 animate-fade-in pointer-events-none">
              <CheckCircleIcon className="w-14 h-14 text-emerald-400 mb-2 animate-bounce" />
              <h4 className="text-white font-bold text-lg sm:text-xl">
                Ad Finished!
              </h4>
              <p className="text-zinc-400 text-xs sm:text-sm mt-1">
                Unlocking your content now...
              </p>
            </div>
          )}

          {/* Progress Bar directly on video base */}
          <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-zinc-800/80">
            <div
              className="h-full bg-secondary transition-all duration-200"
              style={{ width: `${progressPercent}%` }}
            ></div>
          </div>
        </div>

        {/* Bottom Control & Status Bar */}
        <div className="px-4 py-3 bg-[#111111] border-t border-zinc-800 flex items-center justify-between gap-3">
          {/* Audio toggle and remaining time */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={toggleMute}
              className="p-2 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-zinc-300 hover:text-white border border-zinc-800 transition-colors"
              title={isMuted ? "Unmute" : "Mute"}
            >
              {isMuted ? (
                <SpeakerXMarkIcon className="w-4 h-4 text-zinc-400" />
              ) : (
                <SpeakerWaveIcon className="w-4 h-4 text-secondary" />
              )}
            </button>

            <span className="text-xs font-semibold text-zinc-400">
              {remainingSeconds > 0
                ? `Ad • ${remainingSeconds}s remaining`
                : "Ad complete"}
            </span>
          </div>

          {/* Action: Skip Button or Content Unlocked */}
          <div className="flex items-center gap-2">
            {canSkip ? (
              <button
                type="button"
                onClick={handleSkipOrContinue}
                className="px-4 py-2 rounded-xl text-xs sm:text-sm font-bold bg-secondary hover:bg-orange-500 text-white shadow-md shadow-secondary/20 transition-all hover:scale-[1.02] active:scale-95 flex items-center gap-1.5"
              >
                <span>Skip Ad</span>
                <ForwardIcon className="w-4 h-4" />
              </button>
            ) : (
              <span className="px-3 py-1.5 rounded-xl text-xs font-medium text-zinc-500 bg-zinc-900 border border-zinc-800">
                Skip in {skipTimer}s
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
